/**
 * LCP ZIP adapter.
 *
 * An `.lcp` file is a ZIP archive containing `lcp_manifest.json` plus one JSON
 * file per item type, named per {@link ITEM_SCHEMAS}`[type].file` (`frames.json`,
 * `weapons.json`, …). This module's only job is **bytes → {@link RawPack}**: it
 * unzips, reads the manifest and each member JSON, and hands the result to
 * {@link parsePack} for all validation and merge. It does NOT reinvent schema
 * validation.
 *
 * Dependency-light + portable: uses `fflate.unzipSync`, which runs unchanged in
 * Bun and the browser (no Node-only APIs), so this stays frontend-importable.
 */
import { unzipSync, strFromU8 } from "fflate";
import type { RawPack } from "./pack";
import { ITEM_SCHEMAS, ITEM_TYPES, type ItemType } from "./schemas";

const MANIFEST_FILE = "lcp_manifest.json";

/**
 * v3 NPC packs ship one file per class/template, named by item id, with that
 * item's features inlined into the same array. We discover them by these
 * filename prefixes and route each *item* to a `RawPack` array by its `id`
 * prefix (see {@link ID_PREFIX_TO_TYPE}).
 */
const V3_CLASS_FILE_PREFIX = "npcc_";
const V3_TEMPLATE_FILE_PREFIX = "npct_";

/** Maps a v3 item's `id` prefix to its `RawPack` item type. */
const ID_PREFIX_TO_TYPE: ReadonlyArray<readonly [string, ItemType]> = [
  ["npcc_", "npc_class"],
  ["npct_", "npc_template"],
  ["npcf_", "npc_feature"],
];

/** How many unlinked features `zipToRawPack`'s notice names before summarizing the rest. */
const UNLINKED_NOTICE_LIMIT = 5;

/**
 * Thrown when a `.lcp` archive is structurally unusable (not a ZIP, missing or
 * non-JSON manifest, a member file that isn't valid JSON). Distinct from
 * {@link PackValidationError}, which covers schema-level failures downstream:
 * this is "I couldn't even get a {@link RawPack} out of the bytes".
 */
export class LcpArchiveError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "LcpArchiveError";
  }
}

/**
 * A reified, serializable "load pack from ZIP" operation — the shape the command
 * bus (`lvtt pack add <file>`) carries. `bytes` is the raw `.lcp` content;
 * `source` is an optional human label (e.g. the uploaded filename) for
 * diagnostics.
 */
export type PackLoadCommand = {
  type: "pack.load";
  source?: string;
  bytes: Uint8Array;
}

/** Construct a {@link PackLoadCommand} from raw `.lcp` bytes. */
export function packLoadCommand(bytes: Uint8Array, source?: string): PackLoadCommand {
  return { type: "pack.load", source, bytes };
}

/**
 * Map of LCP member filename -> item type, derived from {@link ITEM_SCHEMAS}.
 * Filenames are matched case-insensitively and ignoring any directory prefix
 * inside the archive, so a pack that nests files under a folder still resolves.
 */
const FILE_TO_TYPE: ReadonlyMap<string, ItemType> = (() => {
  const m = new Map<string, ItemType>();
  for (const type of ITEM_TYPES) m.set(ITEM_SCHEMAS[type].file.toLowerCase(), type);
  return m;
})();

/** Strip any directory prefix and lowercase, so `content/frames.json` -> `frames.json`. */
function basename(path: string): string {
  const slash = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  return (slash >= 0 ? path.slice(slash + 1) : path).toLowerCase();
}

function unzip(bytes: Uint8Array): Record<string, Uint8Array> {
  try {
    return unzipSync(bytes);
  } catch (err) {
    throw new LcpArchiveError("Could not read .lcp file: not a valid ZIP archive", {
      cause: err,
    });
  }
}

function parseJsonMember(member: string, raw: Uint8Array): unknown {
  let text: string;
  try {
    text = strFromU8(raw);
  } catch (err) {
    throw new LcpArchiveError(`Could not decode ${member} as UTF-8 text`, { cause: err });
  }
  try {
    return JSON.parse(text);
  } catch (err) {
    throw new LcpArchiveError(`${member} is not valid JSON`, { cause: err });
  }
}

/** Locate `lcp_manifest.json` (basename match, directory-prefix tolerant) and parse it. */
function readManifest(files: Record<string, Uint8Array>): unknown {
  for (const [path, data] of Object.entries(files)) {
    if (basename(path) === MANIFEST_FILE) return parseJsonMember(MANIFEST_FILE, data);
  }
  throw new LcpArchiveError(`Archive is missing ${MANIFEST_FILE}`);
}

/** True if a parsed manifest carries the v3 flag (`"v3": true`). */
function manifestIsV3(manifest: unknown): boolean {
  return (
    typeof manifest === "object" &&
    manifest !== null &&
    (manifest as { v3?: unknown }).v3 === true
  );
}

/** True if any member basename begins with a v3 per-item NPC file prefix. */
function hasV3ItemFiles(files: Record<string, Uint8Array>): boolean {
  for (const path of Object.keys(files)) {
    const name = basename(path);
    if (name.startsWith(V3_CLASS_FILE_PREFIX) || name.startsWith(V3_TEMPLATE_FILE_PREFIX)) {
      return true;
    }
  }
  return false;
}

/** Route a v3 item to a RawPack type by its `id` prefix, or `undefined` if unroutable. */
function v3TypeForId(id: unknown): ItemType | undefined {
  if (typeof id !== "string") return undefined;
  for (const [prefix, type] of ID_PREFIX_TO_TYPE) {
    if (id.startsWith(prefix)) return type;
  }
  return undefined;
}

/** The owner-side feature arrays, as reconstructed from a v3 pack's feature-side edges. */
type V3Groups = { base_features: string[]; optional_features: string[] };

/** A feature that belongs to no class/template group, and why. It is NOT dropped. */
export type V3UnlinkedFeature = { id: string; reason: string };

/** Read a string-valued field off a loosely-typed parsed item, or `undefined`. */
function strField(item: unknown, key: string): string | undefined {
  const v = (item as Record<string, unknown> | null | undefined)?.[key];
  return typeof v === "string" ? v : undefined;
}

/** True if a class/template already ships either owner-side feature array. */
function hasOwnerGroups(owner: unknown): boolean {
  const o = owner as Record<string, unknown> | null | undefined;
  return Array.isArray(o?.base_features) || Array.isArray(o?.optional_features);
}

/**
 * Reconstruct the class/template → feature edges a **v3** pack only expresses on
 * the *feature* side, so downstream code keeps seeing the v2 owner-side shape.
 *
 * The two layouts state the same relationship from opposite ends. v2 puts the
 * edge on the OWNER (`npcc_ace.base_features = ["npcf_missile_launcher_ace", …]`)
 * and gives each feature a decorative `origin: {type, name, base}` whose `name`
 * ("ACE") is a display label, not an id — so the v2 feature side can't be linked
 * back and the owner arrays are the only usable edge. v3 inverts it: the owner
 * carries no arrays at all, and each feature carries `origin: "npcc_ace"` (a real
 * id) plus `base: true` when it's a base feature (absent means optional).
 *
 * Everything past `RawPack` reads the OWNER-side arrays — `FeatureGroups` in the
 * compendium view, and NPC spawn's `base_features` weapon/tech freeze — so the
 * adapter is where v3 has to be normalized onto the v2 shape. That preserves the
 * module invariant: everything downstream of `RawPack` stays format-agnostic.
 *
 * Features are never moved, rewritten or dropped: they stay in `npc_feature`
 * exactly as parsed, and this only ADDS two arrays to the owners. Two guards:
 *
 * - An owner that already supplies **either** array is left untouched, so a pack
 *   shipping v2-style owner arrays through the v3 shredder never gets a second,
 *   derived listing stacked on top of its own. Checked per owner, not per field,
 *   because a half-derived owner could list the same feature twice.
 * - A feature whose `origin` is absent, non-string, or names an id this pack
 *   doesn't define is reported in `unlinked` rather than silently swallowed — it
 *   stays resolvable by id, it just belongs to no group. (The real v3 NPC pack
 *   has exactly one: `npcf_got_your_back_archer`, a `deprecated: true` tombstone.)
 *
 * An owner with no linked features is also left untouched rather than given two
 * empty arrays; every consumer already reads `?? []`, and not-present is the
 * honest statement that this pack said nothing about that owner's features.
 */
function linkV3Features(
  owners: { npc_class: unknown[]; npc_template: unknown[] },
  features: unknown[],
): { npc_class: unknown[]; npc_template: unknown[]; unlinked: V3UnlinkedFeature[] } {
  const ownerIds = new Set<string>();
  for (const o of [...owners.npc_class, ...owners.npc_template]) {
    const id = strField(o, "id");
    if (id !== undefined) ownerIds.add(id);
  }

  const groups = new Map<string, V3Groups>();
  const unlinked: V3UnlinkedFeature[] = [];
  for (const f of features) {
    const id = strField(f, "id");
    if (id === undefined) {
      unlinked.push({ id: "(feature with no string id)", reason: "no string id to link by" });
      continue;
    }
    const origin = (f as Record<string, unknown>).origin;
    if (typeof origin !== "string") {
      unlinked.push({
        id,
        reason:
          origin === undefined
            ? "declares no origin"
            : `origin is ${Array.isArray(origin) ? "an array" : `a ${typeof origin}`}, not a class/template id`,
      });
      continue;
    }
    if (!ownerIds.has(origin)) {
      unlinked.push({ id, reason: `origin "${origin}" names no class or template in this pack` });
      continue;
    }
    const group = groups.get(origin) ?? { base_features: [], optional_features: [] };
    if ((f as Record<string, unknown>).base === true) group.base_features.push(id);
    else group.optional_features.push(id);
    groups.set(origin, group);
  }

  const decorate = (list: unknown[]): unknown[] =>
    list.map((owner) => {
      if (hasOwnerGroups(owner)) return owner;
      const id = strField(owner, "id");
      const group = id === undefined ? undefined : groups.get(id);
      if (group === undefined) return owner;
      return { ...(owner as object), ...group };
    });

  return {
    npc_class: decorate(owners.npc_class),
    npc_template: decorate(owners.npc_template),
    unlinked,
  };
}

/**
 * Read the **v2 (legacy)** `.lcp` layout into a {@link RawPack}: three fixed
 * flat files (`npc_classes.json`/`npc_templates.json`/`npc_features.json`) plus
 * the core item files, each matched by basename against {@link ITEM_SCHEMAS}; an
 * absent member is simply omitted (treated as empty by `parsePack`).
 *
 * Throws {@link LcpArchiveError} for: not-a-ZIP input, a missing or non-JSON
 * `lcp_manifest.json`, or any member file that isn't valid JSON. Schema-level
 * validation is left to {@link parsePack} downstream. This is the v2 branch of
 * {@link zipToRawPack}; call that, not this, unless you specifically want v2.
 */
export function lcpZipToRawPack(bytes: Uint8Array): RawPack {
  const files = unzip(bytes);
  const manifest = readManifest(files);

  const items: RawPack["items"] = {};
  for (const [path, data] of Object.entries(files)) {
    const name = basename(path);
    if (name === MANIFEST_FILE) continue;
    const type = FILE_TO_TYPE.get(name);
    if (type === undefined) continue; // not a recognized item file; ignore extras
    items[type] = parseJsonMember(name, data);
  }

  return { manifest, items };
}

/**
 * Shred the **v3** NPC `.lcp` layout into a {@link RawPack}. v3 packs ship one
 * `npcc_<id>.json` per class and one `npct_<id>.json` per template, each an
 * array of `[the class/template, ...its inlined features]`. We concatenate every
 * such file's items and route each by its `id` prefix (`npcc_`→`npc_class`,
 * `npct_`→`npc_template`, `npcf_`→`npc_feature`) into the same three RawPack
 * arrays the v2 path produces — so everything downstream of `RawPack` is
 * format-agnostic.
 *
 * The class/template → feature edges are then reconstructed from the features'
 * `origin`/`base` fields onto the owners' `base_features`/`optional_features`
 * (see {@link linkV3Features}) — v3 states that relationship only on the feature
 * side, and every consumer past `RawPack` reads it off the owner.
 *
 * A non-item member whose basename is a flat item file in {@link ITEM_SCHEMAS}
 * (`tables.json` → `rollable_table`) is read whole, as the v2 path reads it. Any
 * other non-item member is parked, not silently dropped: its basename is
 * returned in `parked` so the caller can log it. Any item whose
 * `id` doesn't match a known prefix is collected in `unrouted`, and any feature
 * that couldn't be linked to an owner in `unlinked`, for the same reason — no
 * silent truncation.
 */
export function v3ZipToRawPack(bytes: Uint8Array): {
  pack: RawPack;
  parked: string[];
  unrouted: unknown[];
  unlinked: V3UnlinkedFeature[];
} {
  const files = unzip(bytes);
  const manifest = readManifest(files);

  const npc_class: unknown[] = [];
  const npc_template: unknown[] = [];
  const npc_feature: unknown[] = [];
  const buckets: Record<ItemType, unknown[]> = {
    npc_class,
    npc_template,
    npc_feature,
  } as Record<ItemType, unknown[]>;

  const parked: string[] = [];
  const unrouted: unknown[] = [];
  const flat: RawPack["items"] = {};

  for (const [path, data] of Object.entries(files)) {
    const name = basename(path);
    if (name === MANIFEST_FILE) continue;
    const isItemFile =
      name.startsWith(V3_CLASS_FILE_PREFIX) || name.startsWith(V3_TEMPLATE_FILE_PREFIX);
    if (!isItemFile) {
      const flatType = FILE_TO_TYPE.get(name);
      if (flatType !== undefined) flat[flatType] = parseJsonMember(name, data);
      else if (name.endsWith(".json")) parked.push(name);
      continue;
    }
    const parsed = parseJsonMember(name, data);
    if (!Array.isArray(parsed)) {
      throw new LcpArchiveError(
        `Expected v3 NPC file ${name} to be a JSON array, got ${typeof parsed}`,
      );
    }
    for (const item of parsed) {
      const type = v3TypeForId((item as { id?: unknown })?.id);
      if (type === undefined) unrouted.push(item);
      else buckets[type].push(item);
    }
  }

  const linked = linkV3Features({ npc_class, npc_template }, npc_feature);

  const items: RawPack["items"] = { ...flat };
  if (linked.npc_class.length) items.npc_class = linked.npc_class;
  if (linked.npc_template.length) items.npc_template = linked.npc_template;
  if (npc_feature.length) items.npc_feature = npc_feature;

  return { pack: { manifest, items }, parked, unrouted, unlinked: linked.unlinked };
}

/**
 * Single entry point: bytes → {@link RawPack}, absorbing the v2/v3 format split
 * at the RawPack seam so every caller ({@link loadPackFromZip}, the `pack.load`
 * command path, …) gets both formats for free. Detects v3 by the manifest flag
 * (`"v3": true`) **or** the presence of `npcc_`/`npct_` member files, and routes
 * to {@link v3ZipToRawPack}; everything else takes the v2 {@link lcpZipToRawPack}
 * path.
 *
 * Loud-empty guard: a pack that parses but yields zero items throws
 * {@link LcpArchiveError} rather than succeeding silently with an empty pack —
 * the usual cause is an unrecognized/new layout we didn't shred. v3 parked
 * members (non-item files with no {@link ITEM_SCHEMAS} type) and any unroutable items are
 * surfaced via `onNotice`.
 */
export function zipToRawPack(
  bytes: Uint8Array,
  onNotice: (message: string) => void = defaultNotice,
): RawPack {
  const files = unzip(bytes);
  const manifest = readManifest(files);

  let pack: RawPack;
  if (manifestIsV3(manifest) || hasV3ItemFiles(files)) {
    const shredded = v3ZipToRawPack(bytes);
    pack = shredded.pack;
    if (shredded.parked.length) {
      onNotice(`v3 NPC pack: parked non-item file(s) [${shredded.parked.join(", ")}] — not yet ingested.`);
    }
    if (shredded.unrouted.length) {
      onNotice(`v3 NPC pack: ${shredded.unrouted.length} item(s) had an id with no known prefix and were dropped.`);
    }
    if (shredded.unlinked.length) {
      // Not a drop: these features are still in the pack and resolvable by id —
      // they just belong to no class/template feature group. Reported so a real
      // linkage regression can't hide behind a pack's own deprecated tombstones.
      const shown = shredded.unlinked.slice(0, UNLINKED_NOTICE_LIMIT);
      const rest = shredded.unlinked.length - shown.length;
      onNotice(
        `v3 NPC pack: ${shredded.unlinked.length} feature(s) not linked to any class/template ` +
          `(still resolvable by id): ${shown.map((u) => `${u.id} — ${u.reason}`).join("; ")}` +
          (rest > 0 ? `; …and ${rest} more` : ""),
      );
    }
  } else {
    pack = lcpZipToRawPack(bytes);
  }

  const itemCount = Object.values(pack.items).reduce<number>(
    (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
    0,
  );
  if (itemCount === 0) {
    throw new LcpArchiveError(
      "Pack contained no recognizable items. Either the .lcp is empty or its layout " +
        "isn't one this ingester recognizes (v2 flat files or v3 npcc_/npct_ per-item files).",
    );
  }

  return pack;
}

/** Default notice sink for {@link zipToRawPack}: warn to console, never throw. */
function defaultNotice(message: string): void {
  // eslint-disable-next-line no-console
  console.warn(`[compendium] ${message}`);
}
